import { Component, ChangeDetectorRef, ElementRef, OnDestroy, OnInit, HostListener, ViewChild } from "@angular/core";
import { Router } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { CompanyApiService } from "../../services/generated/waba-api/services";
import { ContactApiService } from "../../services/generated/waba-api/services/contact-api.service";
import { SharedLogoHeaderService } from "../../services/header/shared-logo-header.service";
import { initFlowbite } from "flowbite";
import { MetaNotificationApiService } from "../../services/generated/waba-api/services";
import { MetaNotificationConfigApiService } from "../../services/generated/waba-api/services";
import { v4 as uuidv4 } from "uuid";
import { MetaNotification } from "../../services/generated/waba-api/models";
import { interval, Subscription } from "rxjs";
import { UpdateConfig$Params } from "../../services/generated/waba-api/fn/meta-notification-config-api/update-config";
import { LoaderService } from "../../shared/loader/service/loader.service";
import { PendingMessagesService } from "../../services/generated/waba-api/services";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";
import { ensureTranslateLangLoaded } from "../../shared/utils/ensure-translate-lang";
import { translateContactUploadReason } from "../../shared/utils/contact-upload-reason";
import { ContactUploadBatchModalComponent } from "../../components/standalones/contact-upload-batch-modal/contact-upload-batch-modal.component";

const ALL_NOTIFICATION_TYPES = [
  'TEMPLATE_APPROVED', 'TEMPLATE_REJECTED', 'MESSAGE', 'TEXT', 'IMAGE',
  'VIDEO', 'DOCUMENT', 'AUDIO', 'FLOW', 'LOCATION', 'CONTACTS', 'REACTION', 'ORDER',
  'TICKET_MENTION', 'CONTACT_UPLOAD_BATCH_FINISHED', 'BROADCAST_EXPORT_FINISHED'
] as const;
type NotificationType = typeof ALL_NOTIFICATION_TYPES[number];

@Component({
  selector: "app-header",
  templateUrl: "./header.component.html",
  styleUrl: "./header.component.scss",
})
export class HeaderComponent implements OnInit, OnDestroy {
  @ViewChild("notificationToggle") notificationToggle?: ElementRef<HTMLElement>;

  currentLanguage = "fr";
  companyLogoUrl: String | undefined;

  notifications: any[] = [];
  page: number = 0;
  size: number = 10;
  totalElements: number = 0;
  unreadCount: number = 0;
  unreadOnly: boolean = false;
  showNotifications: boolean = false;
  hasWhatsAppAccount: boolean = false;
  notificationDropdownStyle: Record<string, string> = {};
  loadingNotifications: boolean = false;
  markingAllAsRead: boolean = false;
  pollingSubscription: Subscription | undefined;

  // Config modal state
  showConfigModal: boolean = false;
  configEnabledTypes: Set<NotificationType> = new Set();
  allNotificationTypes: readonly NotificationType[] = ALL_NOTIFICATION_TYPES;
  loadingConfig: boolean = false;
  savingConfig: boolean = false;
  expandedNotifications: Set<number> = new Set();

  private objectUrls: string[] = [];

  constructor(
    private translate: TranslateService,
    private companyService: CompanyApiService,
    private sharedLogoHeaderService: SharedLogoHeaderService,
    private metaNotificationService: MetaNotificationApiService,
    private metaNotificationConfigService: MetaNotificationConfigApiService,
    private loaderService: LoaderService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private pendingMessagesService: PendingMessagesService,
    private sanitizer: DomSanitizer,
    private contactApiService: ContactApiService,
    private dialog: MatDialog,
  ) { }

  toggleExpand(event: Event, notificationId: number | undefined) {
    if (notificationId === undefined) return;
    event.stopPropagation();
    if (this.expandedNotifications.has(notificationId)) {
      this.expandedNotifications.delete(notificationId);
    } else {
      this.expandedNotifications.add(notificationId);
    }
    this.cdr.detectChanges();
  }

  isExpanded(notificationId: number | undefined): boolean {
    return notificationId !== undefined && this.expandedNotifications.has(notificationId);
  }

  getTemplateName(description: string | undefined): string {
    if (!description) return "";

    const match = description.match(/Nom:\s*(.*?)(?:\n\s*\n|\n\[|$)/s);
    return match?.[1]?.trim() || "";
  }
  private extractTemplateJson(description: string | undefined): any[] {
    if (!description) return [];

    const start = description.indexOf("[");
    const end = description.lastIndexOf("]");

    if (start === -1 || end === -1 || end < start) {
      return [];
    }

    const jsonStr = description.substring(start, end + 1).trim();

    try {
      return JSON.parse(jsonStr);
    } catch (error) {
      console.error("Failed to parse template description JSON", error, jsonStr);
      return [];
    }
  }


  getTemplateHeaderType(description: string | undefined): string {
    const content = this.extractTemplateJson(description);
    const header = content.find((item: any) => item.type === "HEADER");
    return header?.format || "";
  }


  getTemplateButtons(description: string | undefined): any[] {
    const content = this.extractTemplateJson(description);
    const buttons = content.find((item: any) => item.type === "BUTTONS");
    return Array.isArray(buttons?.buttons) ? buttons.buttons : [];
  }

  getCleanTemplateContent(description: string | undefined): string {
    const content = this.extractTemplateJson(description);
    const bodyItem = content.find((item: any) => item.type === "BODY");

    if (!bodyItem?.text) {
      return "";
    }

    let bodyText = bodyItem.text;

    if (
        bodyItem.example &&
        Array.isArray(bodyItem.example.body_text) &&
        Array.isArray(bodyItem.example.body_text[0])
    ) {
      bodyItem.example.body_text[0].forEach((val: string, index: number) => {
        bodyText = bodyText.split(`{{${index + 1}}}`).join(`<b>${val}</b>`);
      });
    }

    return bodyText.trim();
  }

  getFormattedDescription(description: string | undefined, notificationType?: string): string {
    if (!description) return "";

    const trimmed = description.trim();

    if (notificationType === "TICKET_MENTION") {
      const { ticket, comment } = this.parseTicketMentionDescription(trimmed);
      return this.translate.instant("NOTIFICATION.TICKET_MENTION.MESSAGE", {
        ticket,
        comment,
      });
    }

    if (
      notificationType === "CONTACT_UPLOAD_BATCH_FINISHED" ||
      notificationType === "CONTACT_UPLOAD"
    ) {
      return this.translate.instant("NOTIFICATION.CONTACT_UPLOAD_BATCH.FINISHED");
    }

    if (this.isContactUploadReason(trimmed)) {
      return translateContactUploadReason(trimmed, this.translate);
    }
    if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
      try {
        const obj = JSON.parse(trimmed);
        const lines: string[] = [];
        for (const key of Object.keys(obj)) {
          if (key === 'flow_token') continue;
          const val = obj[key];
          if (Array.isArray(val)) {
            const filesInfo = val.map((item: any) => {
              if (item && typeof item === 'object') {
                const fileName = item.file_name || item.name || '';
                const mimeType = item.mime_type || item.mimeType || '';
                if (mimeType.startsWith('image/')) {
                  return `📷 ${fileName}`;
                } else {
                  return `📄 ${fileName}`;
                }
              }
              return String(item);
            }).join(', ');
            lines.push(filesInfo);
          } else if (val && typeof val === 'object') {
            lines.push(JSON.stringify(val));
          } else {
            lines.push(String(val));
          }
        }
        if (lines.length > 0) {
          const prefix = this.translate.instant('FLOW_META.MEDIA.FLOW_RESPONSE') || 'Flow response:';
          return `${prefix} ${lines.join(', ')}`;
        }
      } catch (e) {
        // Fall back
      }
    }
    return description;
  }

  private parseTicketMentionDescription(description: string): { ticket: string; comment: string } {
    const lines = description.split(/\r?\n/);
    const ticket = (lines[0] || "").replace(/^Ticket\s*/i, "").trim();
    const comment = lines.slice(1).join("\n").trim();
    return {
      ticket: ticket || "-",
      comment: comment || "-",
    };
  }

  private openContactUploadBatchModal(batchReference: string): void {
    this.loaderService.show();
    this.contactApiService.getUploadBatchStatus({
      batchReference,
      "x-api-requestId": uuidv4(),
      "x-api-canal": "web",
    }).subscribe({
      next: (response) => {
        this.loaderService.hide();
        const batchData = response.body;
        if (batchData) {
          ensureTranslateLangLoaded(this.translate).subscribe(() => {
            this.dialog.open(ContactUploadBatchModalComponent, {
              data: { batchData },
              width: "640px",
              minHeight: "300px",
              maxHeight: "85vh",
              hasBackdrop: true,
              panelClass: "contact-upload-batch-modal",
            });
          });
        }
      },
      error: (error) => {
        this.loaderService.hide();
        console.error("Error fetching upload batch status", error);
      },
    });
  }

  private isContactUploadReason(value: string): boolean {
    const normalized = value.trim().toUpperCase();
    const knownTypes = [
      "INVALID_PHONE",
      "DUPLICATE_CONTACT",
      "PHONE_FORMAT",
      "FIXED_NUMBER",
      "PHONE_DUPLICATED",
      "MISSING_FIELDS",
      "MISSING_PHONE",
      "INVALID_MOBILE",
      "MUST_START_WITH_PLUS",
      "PHONE_TOO_SHORT",
      "PHONE_TOO_LONG",
      "INVALID_FORMAT",
    ];
    return knownTypes.includes(normalized) || value.startsWith("CONTACT.");
  }

  ngOnInit() {
    initFlowbite();
    this.currentLanguage = this.translate.currentLang;
    this.sharedLogoHeaderService.logo$.subscribe((logo) => {
      this.companyLogoUrl = logo;
    });
    this.getCompanyLogo();
    this.initializeNotifications();
  }

  private initializeNotifications(): void {
    this.companyService.companyNumbersList({
      "x-api-canal": "web",
      "x-api-requestId": uuidv4(),
    }).subscribe({
      next: (response) => {
        this.hasWhatsAppAccount = (response.body?.length ?? 0) > 0;
        if (this.hasWhatsAppAccount) {
          this.getNotifications();
          this.startPolling();
        }
      },
      error: () => {
        this.hasWhatsAppAccount = false;
        this.showNotifications = false;
      },
    });
  }

  getCurrentLang() {
    return sessionStorage.getItem("lang") || this.translate.currentLang || "fr";
  }

  get isRTL() {
    return this.getCurrentLang() === 'ar';
  }

  changeLanguage(event: Event) {
    const selectElement = event.target as HTMLSelectElement;
    const language = selectElement.value;
    sessionStorage.setItem("lang", language);
    this.translate.use(language);
    setTimeout(() => {
      window.location.reload();
    }, 500);
  }

  getCompanyLogo() {
    this.companyService.getCompanyLogo().subscribe({
      next: (response: any) => {
        if (response && response?.size > 0) {
          this.companyLogoUrl = URL.createObjectURL(response);
        }
      },
      error: (error) => { },
    });
  }

  toggleNotifications() {
    if (!this.hasWhatsAppAccount) return;
    this.showNotifications = !this.showNotifications;
    if (this.showNotifications) {
      this.updateNotificationDropdownPosition();
    }
  }

  private updateNotificationDropdownPosition() {
    const toggle = this.notificationToggle?.nativeElement;
    if (!toggle) return;

    const rect = toggle.getBoundingClientRect();
    const width = 340;
    const margin = 12;
    const left = this.isRTL
      ? Math.min(rect.left, window.innerWidth - width - margin)
      : Math.max(margin, rect.right - width);

    this.notificationDropdownStyle = {
      top: `${rect.bottom + 8}px`,
      left: `${Math.max(margin, left)}px`,
      width: `${width}px`,
    };
  }

  processNotifications(entities: any[]) {
    entities.forEach((notification: any) => {
      if (notification.mediaItems) return; // Already processed

      const desc = notification.description;
      if (desc && desc.trim().startsWith("{") && desc.trim().endsWith("}")) {
        try {
          const obj = JSON.parse(desc.trim());
          const mediaItems: any[] = [];

          for (const key of Object.keys(obj)) {
            if (key === 'flow_token') continue;
            const val = obj[key];
            if (Array.isArray(val)) {
              val.forEach((item: any) => {
                if (item && typeof item === 'object' && (item.id || item.mediaId)) {
                  const mediaId = item.id || item.mediaId;
                  const mimeType = item.mime_type || item.mimeType || '';
                  const fileName = item.file_name || item.name || '';

                  const mediaObj = {
                    mediaId: String(mediaId),
                    mimeType: mimeType,
                    fileName: fileName,
                    safeUrl: null as SafeUrl | null,
                    loading: true,
                    error: false
                  };
                  mediaItems.push(mediaObj);

                  // Fetch media data
                  this.pendingMessagesService.getMediaByMediaId({
                    "x-api-requestId": uuidv4(),
                    "x-api-canal": "web",
                    mediaId: String(mediaId)
                  }).subscribe({
                    next: (blob) => {
                      const objectUrl = URL.createObjectURL(blob);
                      this.objectUrls.push(objectUrl);
                      mediaObj.safeUrl = this.sanitizer.bypassSecurityTrustUrl(objectUrl);
                      mediaObj.loading = false;
                      this.cdr.detectChanges();
                    },
                    error: () => {
                      mediaObj.error = true;
                      mediaObj.loading = false;
                      this.cdr.detectChanges();
                    }
                  });
                }
              });
            }
          }
          if (mediaItems.length > 0) {
            notification.mediaItems = mediaItems;
          }
        } catch (e) {
          // ignore
        }
      }
    });
  }

  getNotifications(append: boolean = false) {
    if (this.loadingNotifications) return;
    this.loadingNotifications = true;

    this.metaNotificationService
      .getNotifications({
        "x-api-requestId": uuidv4(),
        "x-api-canal": "web",
        page: this.page,
        size: this.size,
        unreadOnly: this.unreadOnly,
      })
      .subscribe({
        next: (response) => {
          if (response.body && response.body.notifications) {
            const entities = response.body.notifications.entities || [];
            if (append) {
              const merged = [...this.notifications, ...entities];
              this.processNotifications(merged);
              this.notifications = merged;
            } else {
              this.processNotifications(entities);
              this.notifications = entities;
            }
            this.totalElements = response.body.notifications.totalElements || 0;
            this.unreadCount = response.body.unreadCount || 0;
            this.cdr.detectChanges();
          }
          this.loadingNotifications = false;
          this.cdr.detectChanges();
        },
        error: (error) => {
          this.loadingNotifications = false;
          this.cdr.detectChanges();
        },
      });
  }

  loadMoreNotifications(event: Event) {
    event.stopPropagation();
    this.page++;
    this.getNotifications(true);
  }

  markAsRead(notification: any) {
    const navigate = () => {
      this.showNotifications = false;
      const ref = notification.referenceId;
      const refType = notification.referenceType;
      const notifType = notification.type;

      if (ref && refType === 'CONVERSATION') {
        this.router.navigate(['/team-inbox/page'], { queryParams: { conversationRef: ref } });
      } else if (ref && refType === 'TEMPLATE') {
        this.router.navigate(['/template/request/view', ref]);
      } else if (ref && refType === 'TICKET') {
        this.router.navigate(['/manage-ticket/edit', ref]);
      } else if (ref && (refType === 'CONTACT_UPLOAD_BATCH' || notifType === 'CONTACT_UPLOAD_BATCH_FINISHED')) {
        this.openContactUploadBatchModal(ref);
      } else if (ref && refType === 'BROADCAST_EXPORT') {
        window.open(ref, "_blank", "noopener");
      }
    };

    if (!notification.readStatus && notification.reference) {
      this.metaNotificationService
        .markAsRead({ reference: notification.reference })
        .subscribe({
          next: () => {
            notification.readStatus = true;
            this.updateUnreadCount();
            this.cdr.detectChanges();
            navigate();
          },
          error: (error) => {
            console.error("Error marking as read", error);
            navigate();
          },
        });
    } else {
      navigate();
    }
  }

  updateUnreadCount() {
    this.unreadCount = this.notifications.filter((n) => !n.readStatus).length;
  }

  toggleUnreadOnly() {
    this.unreadOnly = !this.unreadOnly;
    this.page = 0;
    this.notifications = [];
    this.getNotifications();
  }

  markAllAsRead() {
    if (this.markingAllAsRead) return;
    this.markingAllAsRead = true;
    this.cdr.detectChanges();

    this.metaNotificationService.markAllAsRead({}).subscribe({
      next: () => {
        this.notifications.forEach((n) => (n.readStatus = true));
        this.unreadCount = 0;
        this.markingAllAsRead = false;
        this.cdr.detectChanges();
        this.refreshNotifications();
      },
      error: (error) => {
        console.error("Error marking all as read", error);
        this.markingAllAsRead = false;
        this.cdr.detectChanges();
      },
    });
  }

  startPolling() {
    this.pollingSubscription = interval(10000).subscribe(() => {
      this.refreshNotifications();
    });
  }

  stopPolling() {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }
  }

  openConfigModal() {
    this.showConfigModal = true;
    this.showNotifications = false;
    this.loadConfig();
  }

  closeConfigModal() {
    this.showConfigModal = false;
    this.cdr.detectChanges();
  }

  loadConfig() {
    this.loadingConfig = true;
    this.cdr.detectChanges();
    this.metaNotificationConfigService.getConfig({
      "x-api-requestId": uuidv4(),
      "x-api-canal": "web"
    }).subscribe({
      next: (response) => {
        if (response.body) {
          this.configEnabledTypes = new Set((response.body.enabledTypes || []) as NotificationType[]);
        }
        this.loadingConfig = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.loadingConfig = false;
        this.cdr.detectChanges();
      }
    });
  }

  isTypeEnabled(type: NotificationType): boolean {
    return this.configEnabledTypes.has(type);
  }

  toggleType(type: NotificationType) {
    if (this.configEnabledTypes.has(type)) {
      this.configEnabledTypes.delete(type);
    } else {
      this.configEnabledTypes.add(type);
    }
    this.cdr.detectChanges();
  }

  saveConfig() {
    if (this.savingConfig) return;
    this.loaderService.show();
    this.cdr.detectChanges();
    const params: UpdateConfig$Params = {
      "x-api-requestId": uuidv4(),
      "x-api-canal": "web",
      body: { enabledTypes: Array.from(this.configEnabledTypes) as any }
    }
    this.metaNotificationConfigService.updateConfig(params).subscribe({
      next: () => {
        this.savingConfig = false;
        this.showConfigModal = false;
        this.cdr.detectChanges();
        this.loaderService.hide();
      },
      error: () => {
        this.savingConfig = false;
        this.cdr.detectChanges();
        this.loaderService.hide();
      }
    });
  }

  refreshNotifications() {
    const totalSize = (this.page + 1) * this.size;

    this.metaNotificationService
      .getNotifications({
        "x-api-requestId": uuidv4(),
        "x-api-canal": "web",
        page: 0,
        size: totalSize,
        unreadOnly: this.unreadOnly,
      })
      .subscribe({
        next: (response) => {
          if (response.body && response.body.notifications) {
            const entities = response.body.notifications.entities || [];
            this.processNotifications(entities);
            this.notifications = entities;
            this.totalElements = response.body.notifications.totalElements || 0;
            this.unreadCount = response.body.unreadCount || 0;
            this.cdr.detectChanges();
          }
        },
        error: (error) => {
          console.error("Error refreshing notifications", error);
        },
      });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const clickedInsideDropdown = target.closest('.notification-dropdown');
    const clickedToggleButton = target.closest('.notification-toggle-btn');
    if (!clickedInsideDropdown && !clickedToggleButton) {
      this.showNotifications = false;
      this.cdr.detectChanges();
    }
  }

  @HostListener("window:resize")
  onWindowResize() {
    if (this.showNotifications) {
      this.updateNotificationDropdownPosition();
    }
  }

  ngOnDestroy() {
    this.stopPolling();
    this.objectUrls.forEach((url) => URL.revokeObjectURL(url));
  }
}
